/*
  Contract:
    Reveal-on-scroll:
      [data-sp-reveal]                container, observed once
      [data-sp-reveal-item]           children revealed in order (staggered)
      [data-sp-stagger] on container  ms between each item's reveal (default 600)
      .is-revealed                    class added per item when revealed

    Pagination — dots and/or prev/next arrows. Both are only shown while the scrollable element
    actually overflows, so the same markup can be reused by a non-scrolling layout:
      [data-sp-pagination]            wrapper around the scrollable row + the dots/arrows hosts
      [data-sp-pagination-scroll]     the actual scrollable element, inside the wrapper
      [data-sp-pagination-item]       children to paginate between, inside the scrollable element
      [data-sp-pagination-dots]       optional host where the dots get injected — author it `hidden`
      [data-sp-pagination-arrows]     optional wrapper around the arrow buttons — author it `hidden`
      [data-sp-pagination-prev]       optional <button>; scrolls back one item, [disabled] at the start
      [data-sp-pagination-next]       optional <button>; scrolls on one item, [disabled] at the end
      [data-sp-pagination-loop] (on the [data-sp-pagination] container, optional)  prev/next wrap
                                       around to the other end instead of stopping, and never disable.
                                       Dots and swipe/scroll-drag are unaffected — this only changes
                                       what the arrow buttons do at the boundary.
      At least one of the dots host / arrow buttons must be present, and put
      {{ block.shopify_attributes }} on the same element as [data-sp-pagination-item] so the theme
      editor can scroll to the block being edited.

    Media switch (ARIA tabs, activation follows focus):
      [data-sp-media-switch]           container wrapping the triggers + the panels
      [data-sp-media-trigger]          buttons, in order; paired 1:1 with panels by index
      [data-sp-media-panel]            panels, in the SAME order as the triggers
      [data-sp-media-hover] (on the container, optional)  also activate on mouseenter (fine-pointer only)
      .is-active                       class set on the active panel

    Card reveal (overlay toggle over a card's media, one open at a time):
      [data-sp-card-reveal]            container wrapping the cards (can be the same element as
                                        [data-sp-pagination] when the cards are also a carousel)
      [data-sp-card-reveal-card]       each card; receives .is-open
      [data-sp-card-reveal-toggle]     <button> that opens the card's panel; reflects aria-expanded
      [data-sp-card-reveal-panel]      the overlay; aria-hidden when closed
      [data-sp-card-reveal-close]      optional <button> inside the panel that closes it
      [data-sp-card-reveal-hover] (on the container, optional)  also open on mouseenter (fine-pointer only)
      .is-open                         class set on the open card

    Dialog (self-contained modal popup, backed by native <dialog>):
      [data-sp-dialog]                 container wrapping the open trigger + the dialog element
      [data-sp-dialog-open]            <button> inside the container that opens the dialog
      [data-sp-dialog-panel]           the <dialog> element itself — positioning only, no visuals
      [data-sp-dialog-content]         inner wrapper carrying background/padding/scroll; clicking
                                        outside it (the backdrop, or the bare dialog box) closes
      [data-sp-dialog-close]           <button>(s) inside the dialog that close it
      Escape and focus containment come from the native <dialog>; this only wires open/close and
      restores focus to whatever triggered it.
*/
(function () {
  'use strict';

  // True when the OS/browser asks for no motion.
  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Staggers .is-revealed onto each item once the container enters the viewport.
  function initReveal(container) {
    if (container.spRevealInitialized) return;
    container.spRevealInitialized = true;

    var items = Array.prototype.slice.call(container.querySelectorAll('[data-sp-reveal-item]'));
    if (!items.length) return;

    if (prefersReducedMotion()) {
      items.forEach(function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }

    var stagger = parseInt(container.getAttribute('data-sp-stagger'), 10) || 600;

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          items.forEach(function (el, index) {
            setTimeout(function () {
              el.classList.add('is-revealed');
            }, index * stagger);
          });
          obs.unobserve(entry.target);
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(container);
  }

  // Injects a dot per item, wires the optional prev/next arrows, and keeps both in sync with the
  // scroll position. Dots and arrows stay hidden while the row fits, so a section can render the
  // same markup for a scrolling and a non-scrolling layout.
  function initPagination(container) {
    if (container.spPaginationBound) return;

    var scrollEl = container.querySelector('[data-sp-pagination-scroll]');
    if (!scrollEl) return;

    var dotsHost = container.querySelector('[data-sp-pagination-dots]');
    var arrowsHost = container.querySelector('[data-sp-pagination-arrows]');
    var prevButton = container.querySelector('[data-sp-pagination-prev]');
    var nextButton = container.querySelector('[data-sp-pagination-next]');
    if (!dotsHost && !prevButton && !nextButton) return;

    var loop = container.hasAttribute('data-sp-pagination-loop');

    var items = Array.prototype.slice.call(container.querySelectorAll('[data-sp-pagination-item]'));
    if (items.length <= 1) return;

    // Set before any listener is attached: initPagination re-runs on every shopify:section:load,
    // and a row that never overflows would otherwise re-bind on each pass.
    container.spPaginationBound = true;

    var dots = [];

    function hasOverflow() {
      return scrollEl.scrollWidth > scrollEl.clientWidth + 1;
    }

    // 2px tolerance: scrollLeft is fractional on zoomed and HiDPI viewports.
    function isAtStart() {
      return scrollEl.scrollLeft <= 2;
    }

    function isAtEnd() {
      return scrollEl.scrollLeft + scrollEl.clientWidth >= scrollEl.scrollWidth - 2;
    }

    // scrollLeft that puts `index` at the start edge. offsetLeft is measured against a shared
    // offsetParent, so the delta from the first item is the offset we want — correct for any
    // slide width or gap, without reading computed styles.
    function offsetOf(index) {
      return items[index].offsetLeft - items[0].offsetLeft;
    }

    function goTo(index) {
      if (index < 0) index = 0;
      if (index > items.length - 1) index = items.length - 1;
      var left = offsetOf(index);
      if (typeof scrollEl.scrollTo === 'function') {
        scrollEl.scrollTo({ left: left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      } else {
        scrollEl.scrollLeft = left;
      }
    }

    // Item whose start edge is closest to the current scroll position. Used to compute where
    // prev/next should land — NOT for dot display, since with a fractional slides-per-view the
    // trailing items can never reach that edge, so their "nearest" index undershoots the end.
    function nearestIndex() {
      var current = 0;
      var closest = Infinity;
      items.forEach(function (item, index) {
        var distance = Math.abs(offsetOf(index) - scrollEl.scrollLeft);
        if (distance < closest) {
          closest = distance;
          current = index;
        }
      });
      return current;
    }

    // Same as nearestIndex(), except at the very end it reports the last item explicitly so the
    // last dot lights up. Only for dot display — using this to compute a prev-button target would
    // land back on the item nearestIndex() already reports, making prev a no-op at the end.
    function currentIndex() {
      if (isAtEnd()) return items.length - 1;
      return nearestIndex();
    }

    function sync() {
      var current = currentIndex();
      dots.forEach(function (dot, index) {
        dot.setAttribute('aria-current', index === current ? 'true' : 'false');
      });

      var atStart = isAtStart();
      var atEnd = isAtEnd();
      // Looping arrows always wrap, so they never reach a boundary that needs disabling.
      // Disabling the focused button would drop focus to <body>, so hand it to the other arrow.
      if (prevButton) {
        if (loop) {
          prevButton.disabled = false;
        } else {
          if (atStart && nextButton && document.activeElement === prevButton) nextButton.focus();
          prevButton.disabled = atStart;
        }
      }
      if (nextButton) {
        if (loop) {
          nextButton.disabled = false;
        } else {
          if (atEnd && prevButton && document.activeElement === nextButton) prevButton.focus();
          nextButton.disabled = atEnd;
        }
      }
    }

    function buildDots() {
      if (!dotsHost) return;
      dotsHost.innerHTML = '';
      items.forEach(function (item, index) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'sp-pagination__dot';
        dot.setAttribute('aria-label', 'Go to slide ' + (index + 1) + ' of ' + items.length);
        dot.setAttribute('aria-current', 'false');
        dot.addEventListener('click', function () {
          goTo(index);
        });
        dotsHost.appendChild(dot);
      });
      dots = Array.prototype.slice.call(dotsHost.children);
    }

    function render() {
      var overflowing = hasOverflow();
      if (dotsHost) dotsHost.hidden = !overflowing;
      if (arrowsHost) arrowsHost.hidden = !overflowing;
      if (!overflowing) return;
      if (!container.spPaginationBuilt) {
        container.spPaginationBuilt = true;
        buildDots();
      }
      sync();
    }

    if (prevButton) {
      prevButton.addEventListener('click', function () {
        // isAtStart(), not nearestIndex() - 1 < 0: with a fractional slides-per-view
        // nearestIndex() is exact at this edge, but checking the real scroll position
        // (like currentIndex() does) keeps this consistent with the next-button fix below.
        if (loop && isAtStart()) {
          goTo(items.length - 1);
          return;
        }
        goTo(nearestIndex() - 1);
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', function () {
        // isAtEnd(), not nearestIndex() + 1 > items.length - 1: with a fractional
        // slides-per-view nearestIndex() undershoots at the trailing end (see its own
        // comment above), so it never actually reports items.length - 1 there — the old
        // `+ 1` wrap check never fired, and goTo() of the undershot target just clamped
        // back to the same max-scroll position, making the button do nothing.
        if (loop && isAtEnd()) {
          goTo(0);
          return;
        }
        goTo(nearestIndex() + 1);
      });
    }

    var scrollTimeout;
    scrollEl.addEventListener('scroll', function () {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(sync, 100);
    });

    // Exposed so the shopify:block:select listener below can jump to the slide being edited.
    container.spPaginationGoTo = goTo;

    render();

    // ResizeObserver catches width changes the window resize event misses — the theme editor
    // drawer opening, a hidden ancestor becoming visible, late-loading fonts — and fires once
    // on observe, which also covers a row measured at zero width during init.
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(render).observe(scrollEl);
    } else {
      window.addEventListener('resize', render);
    }
  }

  // Pairs triggers 1:1 with panels (ARIA tabs) and follows focus/click/optional-hover; pauses
  // offscreen video, keeps the active panel's video playing.
  function initMediaSwitch(container) {
    if (container.spMediaSwitchInitialized) return;
    container.spMediaSwitchInitialized = true;

    var triggers = Array.prototype.slice.call(container.querySelectorAll('[data-sp-media-trigger]'));
    var panels = Array.prototype.slice.call(container.querySelectorAll('[data-sp-media-panel]'));
    if (triggers.length < 2 || panels.length !== triggers.length) return;

    var allowHover =
      container.hasAttribute('data-sp-media-hover') &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    var activeIndex = 0;
    var isInView = true;

    // A panel may hold more than one <video> (e.g. separate desktop/mobile sources
    // swapped by CSS media query), so every <video> in the panel is played/paused together.
    function playVideo(panel) {
      if (!isInView) return;
      panel.querySelectorAll('video').forEach(function (video) {
        video.play().catch(function () {});
      });
    }

    function pauseVideo(panel) {
      panel.querySelectorAll('video').forEach(function (video) {
        video.pause();
      });
    }

    function activate(index) {
      activeIndex = index;

      triggers.forEach(function (trigger, i) {
        var isActive = i === index;
        trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
        trigger.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      panels.forEach(function (panel, i) {
        var isActive = i === index;
        panel.classList.toggle('is-active', isActive);
        if (isActive) {
          playVideo(panel);
        } else {
          pauseVideo(panel);
        }
      });
    }

    triggers.forEach(function (trigger, index) {
      trigger.addEventListener('click', function () {
        activate(index);
      });
      trigger.addEventListener('focus', function () {
        activate(index);
      });
      if (allowHover) {
        trigger.addEventListener('mouseenter', function () {
          activate(index);
        });
      }
    });

    container.addEventListener('keydown', function (event) {
      var lastIndex = triggers.length - 1;
      var nextIndex = null;

      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        nextIndex = activeIndex === lastIndex ? 0 : activeIndex + 1;
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        nextIndex = activeIndex === 0 ? lastIndex : activeIndex - 1;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = lastIndex;
      } else {
        return;
      }

      event.preventDefault();
      triggers[nextIndex].focus();
    });

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          isInView = entry.isIntersecting;
          if (isInView) {
            playVideo(panels[activeIndex]);
          } else {
            panels.forEach(pauseVideo);
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(container);

    // Exposed so the shopify:block:select listener below can jump to a block being edited.
    container.spMediaSwitchActivate = activate;

    activate(0);
  }

  // One overlay open at a time per container; Escape closes it and returns focus to its toggle.
  function initCardReveal(container) {
    if (container.spCardRevealInitialized) return;
    container.spCardRevealInitialized = true;

    var cards = Array.prototype.slice.call(container.querySelectorAll('[data-sp-card-reveal-card]'));
    if (!cards.length) return;

    var allowHover =
      container.hasAttribute('data-sp-card-reveal-hover') &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    function closeCard(card) {
      card.classList.remove('is-open');
      var toggle = card.querySelector('[data-sp-card-reveal-toggle]');
      var panel = card.querySelector('[data-sp-card-reveal-panel]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      if (panel) panel.setAttribute('aria-hidden', 'true');
    }

    function closeAll() {
      cards.forEach(closeCard);
    }

    function openCard(index) {
      var card = cards[index];
      if (!card) return;
      closeAll();
      card.classList.add('is-open');
      var toggle = card.querySelector('[data-sp-card-reveal-toggle]');
      var panel = card.querySelector('[data-sp-card-reveal-panel]');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      if (panel) panel.setAttribute('aria-hidden', 'false');
    }

    cards.forEach(function (card, index) {
      var toggle = card.querySelector('[data-sp-card-reveal-toggle]');
      var closeButton = card.querySelector('[data-sp-card-reveal-close]');

      if (toggle) {
        toggle.addEventListener('click', function () {
          if (card.classList.contains('is-open')) {
            closeCard(card);
          } else {
            openCard(index);
          }
        });
      }

      if (closeButton) {
        closeButton.addEventListener('click', function () {
          closeCard(card);
          if (toggle) toggle.focus();
        });
      }

      if (allowHover) {
        card.addEventListener('mouseenter', function () {
          openCard(index);
        });
      }
    });

    container.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' && event.code !== 'Escape') return;
      var openCardEl = container.querySelector('[data-sp-card-reveal-card].is-open');
      if (!openCardEl) return;
      var toggle = openCardEl.querySelector('[data-sp-card-reveal-toggle]');
      closeCard(openCardEl);
      if (toggle) toggle.focus();
    });

    // Exposed so the shopify:block:select listener below can jump to the card being edited.
    container.spCardRevealOpen = openCard;
  }

  // Wires an open trigger to a native <dialog>; Escape and focus trapping are native, this just
  // handles open/close and restores focus to the trigger when the dialog closes.
  function initDialog(container) {
    if (container.spDialogInitialized) return;
    container.spDialogInitialized = true;

    var openButton = container.querySelector('[data-sp-dialog-open]');
    var dialog = container.querySelector('[data-sp-dialog-panel]');
    if (!openButton || !dialog) return;

    var closeButtons = Array.prototype.slice.call(dialog.querySelectorAll('[data-sp-dialog-close]'));
    var lastFocused = null;

    function openDialog() {
      lastFocused = document.activeElement;
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
    }

    function closeDialog() {
      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
    }

    openButton.addEventListener('click', openDialog);
    closeButtons.forEach(function (btn) {
      btn.addEventListener('click', closeDialog);
    });

    // Clicking the ::backdrop (or the bare dialog box outside [data-sp-dialog-content]) closes it —
    // e.target is only the dialog itself when the click didn't land on a descendant.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) closeDialog();
    });

    dialog.addEventListener('close', function () {
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    });
  }

  // Entry point: wires up every sp-* reveal/pagination/media-switch/card-reveal/dialog container
  // under `root`.
  function init(root) {
    root = root || document;
    root.querySelectorAll('[data-sp-reveal]').forEach(initReveal);
    root.querySelectorAll('[data-sp-pagination]').forEach(initPagination);
    root.querySelectorAll('[data-sp-media-switch]').forEach(initMediaSwitch);
    root.querySelectorAll('[data-sp-card-reveal]').forEach(initCardReveal);
    root.querySelectorAll('[data-sp-dialog]').forEach(initDialog);
  }

  document.addEventListener('DOMContentLoaded', function () {
    init(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  // Theme editor: bring whichever block the merchant just selected into view.
  document.addEventListener('shopify:block:select', function (event) {
    var paginated = event.target.closest && event.target.closest('[data-sp-pagination]');
    if (paginated && typeof paginated.spPaginationGoTo === 'function') {
      var slides = Array.prototype.slice.call(paginated.querySelectorAll('[data-sp-pagination-item]'));
      var slide = event.target.matches('[data-sp-pagination-item]')
        ? event.target
        : event.target.querySelector('[data-sp-pagination-item]');
      var slideIndex = slides.indexOf(slide);
      if (slideIndex !== -1) paginated.spPaginationGoTo(slideIndex);
    }

    var revealContainer = event.target.closest && event.target.closest('[data-sp-card-reveal]');
    if (revealContainer && typeof revealContainer.spCardRevealOpen === 'function') {
      var cards = Array.prototype.slice.call(revealContainer.querySelectorAll('[data-sp-card-reveal-card]'));
      var card = event.target.matches('[data-sp-card-reveal-card]')
        ? event.target
        : event.target.querySelector('[data-sp-card-reveal-card]');
      var cardIndex = cards.indexOf(card);
      if (cardIndex !== -1) revealContainer.spCardRevealOpen(cardIndex);
    }

    var container = event.target.closest && event.target.closest('[data-sp-media-switch]');
    if (!container || typeof container.spMediaSwitchActivate !== 'function') return;

    var trigger = event.target.matches('[data-sp-media-trigger]')
      ? event.target
      : event.target.querySelector('[data-sp-media-trigger]');
    if (!trigger) return;

    var triggers = Array.prototype.slice.call(container.querySelectorAll('[data-sp-media-trigger]'));
    var index = triggers.indexOf(trigger);
    if (index !== -1) {
      container.spMediaSwitchActivate(index);
    }
  });
})();
